// A live campaign can move between public discovery and a private,
// invitation-only collaboration. The confirmation spells out who keeps
// access before the public page and creator feed change.

import {useEffect} from 'react';
import type {BrandCampaign, CampaignAccess} from '../../shared/brand-api';
import {Icon} from '../creator/components/Icon';

export function CampaignAccessDialog({campaign, busy, onCancel, onConfirm}: {
  campaign: BrandCampaign;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (access: CampaignAccess) => void;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);
  const makePrivate = campaign.access_mode === 'open';
  const paused = campaign.status === 'paused';
  const next: CampaignAccess = makePrivate ? 'invited' : 'open';
  return <div className="switch-layer">
    <div className="switch-backdrop" onClick={onCancel} />
    <div className="card switch-dialog" role="dialog" aria-modal="true" aria-labelledby="campaign-access-title">
      <button className="switch-close" type="button" onClick={onCancel} aria-label="Close"><Icon name="x" size={18} /></button>
      <p className="eyebrow">Campaign visibility</p>
      <h2 id="campaign-access-title">Make “{campaign.title}” {makePrivate ? 'private' : 'public'}?</h2>
      <p className="switch-note"><Icon name={makePrivate ? 'lock' : 'eye'} size={16} /><span>{makePrivate
        ? `Its public page stops working, and matching creators ${paused ? 'will not discover it when you resume' : 'can no longer discover or apply to it'}.`
        : paused
          ? 'Its public page returns with a closed notice. Matching creators can discover and join or apply once you resume.'
          : 'Its public page returns, and matching creators can discover and join or apply again.'}</span></p>
      <p className="switch-note"><Icon name="users" size={16} /><span>{makePrivate
        ? 'Creators already in keep access. Pending invitees remain invited; you can invite or select more whenever the campaign is live.'
        : 'Creators you removed stay excluded, even when the campaign is public.'}</span></p>
      <div className="switch-actions">
        <button className="btn btn-light" type="button" onClick={onCancel}>Cancel</button>
        <button className="btn btn-g" type="button" disabled={busy} onClick={() => onConfirm(next)}>
          {busy ? 'Saving…' : makePrivate ? 'Make private' : 'Make public'}
        </button>
      </div>
    </div>
  </div>;
}
